import clsx from "clsx";
import { FunctionComponent } from "react";

import Button from "@ui/components/cta/Button";
import * as toggleStyles from "@ui/components/cta/ToggleCompact/ToggleCompact.css";
import CloseIcon from "@ui/components/icon/CloseIcon";
import { SELECTORS } from "@ui/config";
import { sharedStyles } from "@ui/shared-styles";

import * as styles from "./Regwall.css";

export interface Props {
  className?: string;
}

/**
 * Regwall (Registration Wall) component displayed to users who need
 * to create a free account to access content.

 * Contains three screens:
 * 1. CTA - Call to action with login button
 * 2. Login - Embedded iframe with login form
 * 3. Acknowledgement - Success message after registration, shows newsletter signup
 *
 * Screen visibility is controlled via JavaScript in the `regwall.js` script.
 * The login iframe communicates via postMessage on success.
 */
const Regwall: FunctionComponent<Props> = ({ className }) => {
  return (
    <div className={className}>
      {/* Semi-transparent overlay covering the page */}
      <div className={styles.overlay} data-regwall-overlay />

      {/* Main regwall container fixed to bottom of screen */}
      <div
        id="zephr-footer-body"
        className={styles.container}
        data-regwall-container
      >
        <div
          className={clsx(
            sharedStyles.layout.fancyBorder,
            styles.borderContainer,
          )}
        >
          <div className={styles.content}>
            {/* Screen 1: Call to Action */}
            <button
              type="button"
              className={styles.closeButton}
              aria-label="Close"
              data-regwall-close-btn
              data-regwall-soft-close-btn
            >
              <CloseIcon width={18} height={18} />
            </button>
            <div className={styles.screen} data-regwall-screen="cta">
              <h2 className={styles.heading} data-regwall-title>
                {"{{{cta-title-text}}}"}
              </h2>
              <p className={styles.description}>{"{{{cta-body-text}}}"}</p>
              <div className={styles.buttonsContainer}>
                <Button
                  id="zephr-footer-body-button"
                  type="button"
                  text="{{{cta-button-register-text}}}"
                  displayType="account"
                  fullWidth
                  className={styles.button}
                  data-regwall-login-btn
                  data-regwall-action="register"
                />
                <div className={styles.divider}>or</div>
                <Button
                  type="button"
                  text="{{{cta-button-subscribe-text}}}"
                  displayType="auth"
                  fullWidth
                  className={styles.button}
                  data-regwall-subscribe-btn
                  data-regwall-action="subscribe"
                  data-regwall-subscribe-url="{{{cta-button-subscribe-url}}}"
                />
              </div>
              <div className={styles.smallTextContainer}>
                <p className={clsx(styles.smallText, styles.smallTextSignIn)}>
                  {"{{{sign-in-cta-text}}}"}{" "}
                  <a
                    className={styles.signInLink}
                    href="/auth/login"
                    data-regwall-login-btn
                    data-regwall-action="sign in"
                  >
                    Sign In
                  </a>
                </p>
                {/* Show back to homepage on hard regwall */}
                <p className={styles.smallText} data-regwall-homepage-cta>
                  Back to{" "}
                  <a
                    href="/"
                    className={styles.homepageLink}
                    data-regwall-homepage-link
                    data-regwall-action="back to homepage"
                  >
                    {"{{{link-homepage-text}}}"}
                  </a>
                </p>
                {/* Show extra CTA link on soft regwall */}
                <p className={styles.smallText} data-regwall-extra-cta>
                  <a
                    href={"{{{link-extra-cta-url}}}"}
                    className={styles.homepageLink}
                    data-regwall-extra-cta-url
                    data-regwall-action="why register"
                  >
                    {"{{{link-extra-cta-text}}}"}
                  </a>
                </p>
              </div>
            </div>

            {/* Screen 2: Login iframe */}
            <div className={styles.screen} data-regwall-screen="login">
              <div
                className={styles.iframeContainer}
                data-regwall-iframe-container
              >
                <iframe
                  src="/auth/embedded-login"
                  className={styles.loginFrame}
                  title="Login or Register"
                  data-regwall-iframe
                />
              </div>
              <div className={styles.smallTextContainer}>
                <p className={styles.smallText} data-regwall-homepage-cta>
                  Back to{" "}
                  <a
                    href="/"
                    className={styles.homepageLink}
                    data-regwall-homepage-link
                    data-regwall-action="back to homepage"
                  >
                    {"{{{link-homepage-text}}}"}
                  </a>
                </p>
              </div>
            </div>

            {/* Screen 3: Acknowledgement */}
            <div
              className={styles.screen}
              data-regwall-screen="acknowledgement"
            >
              <button
                type="button"
                className={styles.closeButton}
                aria-label="Close"
                data-regwall-close-btn
              >
                <CloseIcon width={18} height={18} />
              </button>
              <div className={styles.acknowledgementContent}>
                <div className={styles.acknowledgementHeadingContainer}>
                  <h2 className={styles.heading}>
                    {"{{{confirmation-title-text}}}"}
                  </h2>
                  <p className={styles.description}>
                    {"{{{confirmation-body-text}}}"}
                  </p>
                </div>

                {/* Newsletter cards are dynamically rendered by regwall.js */}
                <div
                  className={styles.newsletterList}
                  data-regwall-newsletter-list
                />

                {/* Template for newsletter cards - cloned by regwall.js and filled with newsletter data */}
                <template data-regwall-newsletter-template>
                  <div className={styles.newsletterCard} data-newsletter-card>
                    <div className={styles.newsletterHeader}>
                      {/* eslint-disable-next-line jsx-a11y/heading-has-content */}
                      <h3
                        className={styles.newsletterTitle}
                        data-newsletter-title
                      />
                      <label
                        className={`${SELECTORS.ToggleCompact} ${toggleStyles.container}`}
                        aria-label="Subscribe to newsletter"
                      >
                        <input
                          type="checkbox"
                          className={toggleStyles.checkbox}
                          checked
                        />
                        <div className={toggleStyles.toggleOffIcon}>
                          <svg
                            viewBox="0 0 17 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                          >
                            <path
                              className={toggleStyles.iconPath}
                              d="M9.667 3.333a1.167 1.167 0 0 0-2.334 0v3.5h-3.5a1.167 1.167 0 0 0 0 2.334h3.5v3.5a1.167 1.167 0 0 0 2.334 0v-3.5h3.5a1.167 1.167 0 1 0 0-2.334h-3.5v-3.5Z"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <div className={toggleStyles.toggleOnIcon}>
                          <svg
                            viewBox="0 0 17 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                          >
                            <g clipPath="url(#toggleCompactCheck)">
                              <path
                                className={toggleStyles.iconPathChecked}
                                d="M15.142 2.056a1.381 1.381 0 0 0-.981.473c-2.764 3-4.398 4.896-7.068 7.84L3.82 7.54a1.355 1.355 0 0 0-1.05-.33 1.406 1.406 0 0 0-.957.572c-.493.667-.393 1.64.223 2.173l4.281 3.708a1.348 1.348 0 0 0 1.9-.115c3.074-3.336 4.945-5.563 7.96-8.835A1.64 1.64 0 0 0 16.5 3c-.23-.588-.768-.963-1.358-.944Z"
                              />
                            </g>
                            <defs>
                              <clipPath id="toggleCompactCheck">
                                <path fill="white" d="M.5 0h16v16H.5z" />
                              </clipPath>
                            </defs>
                          </svg>
                        </div>
                      </label>
                    </div>
                    <p
                      className={styles.newsletterDescription}
                      data-newsletter-description
                    />
                  </div>
                </template>

                <Button
                  type="button"
                  text="Sign Up and Continue Reading"
                  displayType="account"
                  fullWidth
                  className={styles.acknowledgementButton}
                  data-regwall-submit-btn
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Load regwall script */}
      {/* Note (mcrider): This script only exists in Vox's `public/paywall-scripts/` folder; we should
      symlink it from a common place into each app's public folder when we need it for other brands
      (or add a build step that copies it to each app) */}
      <script id="regwall-script" src="/paywall-scripts/regwall.js" />

      {/* Initialize regwall with template variables */}
      <script
        dangerouslySetInnerHTML={{
          __html: `
            (function() {
              // Parse boolean template from string to actual boolean value
              var parseBoolean = function(value) {
                 if (typeof value === 'boolean') return value;
                 return value.toLowerCase() === 'true';
              };

              var initRegwall = function() {
                new ZephrRegwallController({
                  analyticsRuleName: "{{analytics-rule-name}}",
                  itmCampaign: "{{itm_campaign}}",
                  itmMedium: "{{itm_medium}}",
                  itmSource: "{{itm_source}}",
                  bodyText: "{{cta-body-text}}",
                  isRegistered: parseBoolean("{{{is_registered}}}"),
                  isSoftRegwall: parseBoolean("{{{is_soft_regwall}}}"),
                }).init();
              };

              if (typeof ZephrRegwallController !== "undefined") {
                // Script already loaded
                initRegwall();
                return;
              }

              // Otherwise wait for the script to load
              var script = document.getElementById("regwall-script");
              if (script) {
                script.addEventListener("load", function() {
                  initRegwall();
                });
              }
            })();
          `,
        }}
      />
    </div>
  );
};

export default Regwall;
